import { useEffect, useRef } from "react";
import { useAtomValue } from "jotai";

import { cn, PageHeader, Panel } from "@go-novus/ui";

import { conversationListAtom } from "#/features/conversations";
import { storeConnectionAtom } from "#/features/store-connection";
import * as m from "#/paraglide/messages";

import { ConversationList } from "./conversation-list";
import { ConversationReplay } from "./conversation-replay";
import { InboxNotice } from "./inbox-notice";

// 窄屏一次只看一样：并排放不下时把列表和回放叠在一起，点一条只换掉折叠线以下的东西，
// 商家看不出自己点成功了。选中态记在 URL 上，藏哪一块跟着它走，系统返回键天然能退回列表。
const HIDE_NARROW = "@max-[44rem]:hidden";

export interface InboxPageProps {
  onSelectConversation: (conversationId: string | undefined) => void;
  selectedConversationId: string | undefined;
}

/** 左边真实买家会话列表，右边选中会话的只读回放；选中的会话记在 URL 上。 */
export function InboxPage({
  onSelectConversation,
  selectedConversationId,
}: InboxPageProps) {
  const timeZone = useAtomValue(storeConnectionAtom).data?.timezone;
  // 平铺列表按最近活跃排序，自动选中第一条会话。
  const firstConversationId =
    useAtomValue(conversationListAtom).data?.pages[0]?.items[0]?.id;
  const initializedSelection = useRef(false);

  useEffect(() => {
    if (initializedSelection.current) return;
    if (selectedConversationId !== undefined) {
      initializedSelection.current = true;
    } else if (firstConversationId !== undefined) {
      // 只在进入页面时自动选择，返回列表后不再次打开回放。
      initializedSelection.current = true;
      onSelectConversation(firstConversationId);
    }
  }, [firstConversationId, onSelectConversation, selectedConversationId]);

  const replaying = selectedConversationId !== undefined;

  return (
    <div className="flex h-full min-h-0 flex-col gap-5.5">
      {/* 窄屏进了回放就收起页头：回放自己那条已经写着在看谁，底部导航也还标着 Inbox。 */}
      <div className={replaying ? HIDE_NARROW : undefined}>
        <PageHeader title={m.inbox_page_title()} />
      </div>
      {/* 共用外框；中等宽度左栏 350px，宽屏 400px，窄屏一次只显示一侧。 */}
      <Panel className="grid min-h-0 flex-1 @min-[44rem]:grid-cols-[21.875rem_minmax(0,1fr)] @min-[60rem]:grid-cols-[25rem_minmax(0,1fr)]">
        <div
          className={cn(
            "grid min-h-0 min-w-0 @min-[44rem]:border-r border-[var(--border-surface-default)]",
            replaying && HIDE_NARROW,
          )}
        >
          <ConversationList
            onSelect={onSelectConversation}
            selectedConversationId={selectedConversationId}
            timeZone={timeZone}
          />
        </div>
        <div
          className={cn(
            "grid min-h-0 min-w-0 bg-[var(--bg-surface-base)]",
            !replaying && HIDE_NARROW,
          )}
        >
          {selectedConversationId === undefined ? (
            <div className="flex min-h-0 flex-col justify-center">
              <InboxNotice
                body={m.inbox_replay_empty_body()}
                heading={m.inbox_replay_empty_heading()}
              />
            </div>
          ) : (
            <ConversationReplay
              conversationId={selectedConversationId}
              onBack={() => {
                onSelectConversation(undefined);
              }}
              timeZone={timeZone}
            />
          )}
        </div>
      </Panel>
    </div>
  );
}
